import { useCallback, useState, useEffect, useRef } from 'react';
import { OrderBy, SortBy } from 'shared/types/query/ReviewQuery';
import { Review, ReviewStats } from 'shared/types/review/Review';
import { sdk } from 'sdk';
import { PaginatedResult } from 'shared/types/result';
import { UseReviewsReturn } from './types';
import { ReviewRatingStatistics } from 'shared/types/product/Product';
import { mapReviewStats } from './utils';

const REVIEWS_PER_PAGE = 3;

/**
 * Hook for managing product reviews
 *
 * @param initialProductId        Optional product ID to fetch reviews for on initial render
 * @param initialReviewStats      Optional review statistics from the product, now using ReviewRatingStatistics
 * @returns                       Object with reviews data, statistics, and methods for fetching and submitting reviews
 */
const useReviews = (initialProductId?: string, initialReviewStats?: ReviewRatingStatistics): UseReviewsReturn => {
  const [reviews, setReviews] = useState<Review[]>([]);

  // Initialize reviewStats using mapReviewStats
  const [reviewStats, setReviewStats] = useState<ReviewStats>(() => mapReviewStats(initialReviewStats));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Pagination and sorting state
  const [currentProductId, setCurrentProductId] = useState<string | undefined>(initialProductId);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [currentSort, setCurrentSort] = useState<SortBy>('createdAt');
  const [currentOrder, setCurrentOrder] = useState<OrderBy>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Refs for tracking initialization and pending requests
  const initializedRef = useRef(false);
  const fetchInProgressRef = useRef(false);

  /**
   * Fetch reviews for a specific product
   *
   * @param productId - ID of the product to fetch reviews for
   * @param sortBy - Field to sort by (default: 'createdAt')
   * @param orderBy - Sort direction (default: 'desc')
   */
  const fetchReviews = useCallback(
    async (productId: string, sortBy: SortBy = 'createdAt', orderBy: OrderBy = 'desc') => {
      if (!productId || fetchInProgressRef.current) return;

      fetchInProgressRef.current = true;
      setIsLoading(true);
      setError(null);
      setCurrentProductId(productId);
      setCurrentSort(sortBy);
      setCurrentOrder(orderBy);
      setCurrentPage(1);

      try {
        const response = await sdk.composableCommerce.review.query({
          productId,
          sortBy,
          orderBy,
          limit: REVIEWS_PER_PAGE,
          page: 1,
        });

        if (response.isError) {
          throw new Error(response.error?.message || 'Error fetching reviews');
        }

        const result = response.data as PaginatedResult<Review>;

        setReviews(result.items);
        setNextCursor(result.nextCursor);

        // Calculate if there are more reviews to load
        // Use reviewStats.totalCount which we already have from the product
        setHasMore(result.items.length < reviewStats.totalCount);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
        fetchInProgressRef.current = false;
      }
    },
    [reviewStats.totalCount],
  );

  /**
   * Load more reviews (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!currentProductId || isLoading || !hasMore) return;

    setIsLoading(true);
    const nextPage = currentPage + 1;

    try {
      const response = await sdk.composableCommerce.review.query({
        productId: currentProductId,
        sortBy: currentSort,
        orderBy: currentOrder,
        limit: REVIEWS_PER_PAGE,
        page: nextPage,
        cursor: nextCursor,
      });

      if (response.isError) {
        throw new Error(response.error?.message || 'Error loading more reviews');
      }

      const result = response.data as PaginatedResult<Review>;

      // Append new reviews to existing ones
      const newReviews = [...reviews, ...result.items];
      setReviews(newReviews);
      setNextCursor(result.nextCursor);

      // Update hasMore based on total count from stats
      setHasMore(newReviews.length < reviewStats.totalCount);
      setCurrentPage(nextPage);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentProductId,
    nextCursor,
    isLoading,
    currentSort,
    currentOrder,
    reviews,
    reviewStats.totalCount,
    currentPage,
  ]);

  /**
   * Submit a new review
   *
   * @param reviewData - Data for the new review
   * @returns Promise resolving to true if successful, false otherwise
   */
  const submitReview = useCallback(
    async (reviewData: { rating: number; title: string; review?: string; displayName: string }) => {
      if (!currentProductId) {
        setError(new Error('Product ID is required to submit a review'));
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Validate required fields before sending to API
        if (!reviewData.title || !reviewData.rating || !reviewData.displayName) {
          throw new Error('All review fields are required');
        }

        // Map fields to match API expectations
        const reviewPayload = {
          productId: currentProductId,
          title: reviewData.title,
          description: reviewData.review,
          rating: reviewData.rating,
          authorName: reviewData.displayName,
        };

        const response = await sdk.composableCommerce.review.create(reviewPayload);

        if (response.isError) {
          throw new Error(response.error?.message || 'Error submitting review');
        }

        // Refresh reviews to include the new one
        // (we don't need to update stats as they'll be updated when product is refreshed)
        await fetchReviews(currentProductId, currentSort, currentOrder);

        return true;
      } catch (err) {
        setError(err as Error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [currentProductId, fetchReviews, currentSort, currentOrder],
  );

  // Update reviewStats when initialReviewStats changes
  useEffect(() => {
    if (initialReviewStats) {
      setReviewStats(mapReviewStats(initialReviewStats));
    }
  }, [initialReviewStats]);

  // Initialize reviews once if productId is provided
  useEffect(() => {
    if (initialProductId && !initializedRef.current && !isLoading) {
      initializedRef.current = true;
      fetchReviews(initialProductId);
    }
  }, [initialProductId, fetchReviews, isLoading]);

  return {
    reviews,
    reviewStats,
    isLoading,
    error,
    fetchReviews,
    submitReview,
    loadMore,
    hasMore,
  };
};

export default useReviews;
